import { EVENTS, subscribe } from '@utilities/events';
import { isVariantFullyAvailable } from '@utilities/product';

/**
 * ProductForm
 * Manages form submission, ATC button state, sticky add-to-bag behaviour,
 * preorder messaging, and minicart message line-item properties.
 *
 * Relies on its parent <product-overview> for product/variant state.
 * Subscribes to (via pub-sub):
 *   - product-overview:initialized
 *   - product-overview:variant-change
 *   - product-overview:no-variant
 *   - product-promotion:change  (promotion line-item props)
 */
if (!customElements.get('product-form')) {
  customElements.define('product-form', class ProductForm extends HTMLElement {
    constructor() {
      super();

      /** @type {HTMLElement|null} */
      this.productOverview = null;

      /** Promotion data collected from <product-promotion> events */
      this.promotion = {};

      // Sticky ATC
      this.boundHandleStickyATC = this.handleStickyATC.bind(this);
      this.boundHandleResize = this.handleResize.bind(this);
      this.atbButtonMarginOffset = 150;

      // Bound handlers
      this.boundHandleInitialized = this.handleInitialized.bind(this);
      this.boundHandleVariantChange = this.handleVariantChange.bind(this);
      this.boundHandleNoVariant = this.handleNoVariant.bind(this);
      this.boundHandlePromotionChange = this.handlePromotionChange.bind(this);
      this.boundHandleFormSubmit = this.onFormSubmit.bind(this);
    }

    // ─── Debug ──────────────────────────────────────────────
    allowDebug() {
      return this.productOverview?.classList.contains('debug') ?? false;
    }

    // ─── Lifecycle ──────────────────────────────────────────
    connectedCallback() {
      this.productOverview = this.closest('product-overview');
      if (!this.productOverview) {
        console.warn('ProductForm: No <product-overview> parent found.');
        return;
      }

      this.cacheElements();

      // Read sticky ATB config from the parent element's data attributes
      this.enableStickyATB = this.productOverview.dataset.enableStickyAtb === 'true';
      this.enableStickyATBon = this.productOverview.dataset.enableStickyAtbOn || 'render';

      // If parent already initialised, run immediately
      if (this.productOverview.classList.contains('product-overview--initialized')) {
        this.onReady(this.productOverview.product, this.productOverview.variant);
      }

      this.subscribeToEvents();
    }

    disconnectedCallback() {
      this.unsubscribeFromEvents();
      this.disableStickyATBEvents();
      this.productOverview = null;
    }

    // ─── Element Cache ──────────────────────────────────────
    cacheElements() {
      this.form = this.querySelector('.product__form');
      this.atcButton = this.querySelector('button[data-add-to-cart]');
      this.productActions = this.querySelector('.product__form-actions');
      this.preorderMessage = this.querySelector('.product__preorder-message');
      this.preorderMessageProperty = this.querySelector('input[name="properties[_po_message]"]');
      this.minicartMessageProperty = this.querySelector('input[name="properties[_minicart_message]"]');
    }

    // ─── Subscriptions ─────────────────────────────────────
    subscribeToEvents() {
      this.unsubscribers = [
        subscribe(EVENTS.PRODUCT_OVERVIEW_INITIALIZED, this.boundHandleInitialized),
        subscribe(EVENTS.PRODUCT_OVERVIEW_VARIANT_CHANGE, this.boundHandleVariantChange),
        subscribe(EVENTS.PRODUCT_OVERVIEW_NO_VARIANT, this.boundHandleNoVariant),
        subscribe(EVENTS.PRODUCT_PROMOTION_CHANGE, this.boundHandlePromotionChange),
      ];

      this.addEventListener('submit', this.boundHandleFormSubmit);
      this.toggleStickyATBFunctionality();
    }

    unsubscribeFromEvents() {
      if (this.unsubscribers) {
        this.unsubscribers.forEach((unsub) => unsub());
        this.unsubscribers = [];
      }
      this.removeEventListener('submit', this.boundHandleFormSubmit);
    }

    // ─── Event Handlers ─────────────────────────────────────
    handleInitialized(data) {
      if (data.productOverview !== this.productOverview) return;
      this.onReady(data.product, data.variant);
    }

    handleVariantChange(data) {
      if (data.productOverview !== this.productOverview) return;
      const { variant } = data;
      const product = this.productOverview?.product;
      const noVariant = !variant || Object.keys(variant).length === 0;

      this.handleErrorMessage();

      if (noVariant) {
        this.updateATCButton(false, 'Product not available', product);
        this.updatePreorderText(false);
        this.updateMiniCartMessageInput(false);
        return;
      }

      this.updateATCButton(isVariantFullyAvailable(variant), null, product);
      this.updatePreorderText(variant.show_preorder_message);
      this.updateMiniCartMessageInput(variant.show_minicart_message);
      this.updateVariantSelectionOnButtons(variant);
    }

    handleNoVariant(data) {
      if (data.productOverview !== this.productOverview) return;
      const product = this.productOverview?.product;
      this.updateATCButton(false, 'Product not available', product);
      this.updatePreorderText(false);
      this.updateMiniCartMessageInput(false);
    }

    handlePromotionChange(data) {
      if (!this.productOverview || !this.productOverview.contains(data.triggeringElement)) return;
      this.promotion = data.promotion;
    }

    // ─── Initial render when parent is ready ────────────────
    onReady(product, variant) {
      if (!variant || Object.keys(variant).length === 0) {
        this.updateATCButton(false, 'Product not available', product);
        return;
      }
      this.updateATCButton(isVariantFullyAvailable(variant), null, product);
      this.updatePreorderText(variant.show_preorder_message);
      this.updateMiniCartMessageInput(variant.show_minicart_message);
      this.updateVariantSelectionOnButtons(variant);
    }

    // ─── ATC Button ─────────────────────────────────────────
    updateATCButton(variantAvailable, overrideText, product) {
      if (!this.atcButton) return;
      const atcButtonText = this.atcButton.querySelector('.button__text');
      if (!atcButtonText) return;

      const noVariant = this.productOverview?.noVariant ?? false;

      if (variantAvailable) {
        atcButtonText.innerText = this.atcButton.dataset.availableText;
        this.atcButton.removeAttribute('disabled');
        this.atcButton.removeAttribute('aria-disabled');
        this.atcButton.dataset.state = 'add-to-bag';
      } else {
        atcButtonText.innerText = this.atcButton.dataset.unavailableText;
        this.atcButton.setAttribute('disabled', true);
        this.atcButton.setAttribute('aria-disabled', true);
        this.atcButton.dataset.state = 'sold-out';
      }

      if (overrideText) {
        atcButtonText.innerText = overrideText;
      }

      // Loop Returns eligibility check
      if (
        product?.third_parties?.loop_returns_eligible === false &&
        window.LoopOnstore?.isActive() === true
      ) {
        atcButtonText.innerText = 'This product is not eligible for Loop On-Store Returns';
        this.atcButton.setAttribute('disabled', true);
        this.atcButton.setAttribute('aria-disabled', true);
      }

      this.atcButton.classList.toggle('no-variant', !!noVariant);
    }

    updateVariantSelectionOnButtons(variant) {
      if (!this.atcButton || !this.productActions) return;

      let selectedVariantPriceHtml = '';
      if (variant && Object.keys(variant).length > 0 && variant.price_html) {
        selectedVariantPriceHtml = variant.price_html;
      }

      const atcButtonTexts = this.productActions.querySelectorAll('.button__text');
      atcButtonTexts.forEach((atcButtonText) => {
        const existingSpan = atcButtonText.querySelector('.atc-variant-info');
        if (existingSpan) {
          existingSpan.innerHTML = selectedVariantPriceHtml;
        } else {
          const span = document.createElement('span');
          span.className = 'atc-variant-info desktop-hidden';
          span.innerHTML = selectedVariantPriceHtml;
          atcButtonText.append(span);
        }
      });
    }

    setAddingToBagState(isAdding) {
      if (!this.atcButton) return;
      const buttonText = this.atcButton.querySelector('.button__text');
      const spinner = this.atcButton.querySelector('.loading-overlay__spinner');

      this.atcButton.classList.toggle('adding-to-bag', isAdding);
      this.atcButton.toggleAttribute('disabled', isAdding);
      this.atcButton.toggleAttribute('aria-disabled', isAdding);
      this.atcButton.classList.toggle('loading', isAdding);

      if (buttonText) {
        buttonText.innerText = isAdding
          ? 'Adding to bag'
          : this.atcButton.dataset.availableText;
      }
      if (spinner) {
        spinner.classList.toggle('hidden', !isAdding);
      }

      if (!isAdding) {
        const variant = this.productOverview?.variant;
        const product = this.productOverview?.product;
        if (variant) {
          this.updateATCButton(isVariantFullyAvailable(variant), null, product);
          this.updateVariantSelectionOnButtons(variant);
        }
      }
    }

    // ─── Preorder / Minicart Messaging ──────────────────────
    updatePreorderText(showPreorderMessage) {
      if (showPreorderMessage) {
        this.preorderMessage?.classList.remove('hidden');
        this.preorderMessageProperty?.removeAttribute('disabled');
      } else {
        this.preorderMessage?.classList.add('hidden');
        this.preorderMessageProperty?.setAttribute('disabled', true);
      }
    }

    updateMiniCartMessageInput(enableMinicartMessage) {
      if (enableMinicartMessage) {
        this.minicartMessageProperty?.removeAttribute('disabled');
      } else {
        this.minicartMessageProperty?.setAttribute('disabled', true);
      }
    }

    // ─── Form Submission ────────────────────────────────────
    onFormSubmit(event) {
      if (event.target.matches('.product__form')) {
        event.preventDefault();
        this.submitToCart();
      }
    }

    /**
     * Collect line-item properties from hidden inputs within this form's
     * parent <product-overview>, plus promotion properties.
     */
    collectLineItemProperties() {
      const container = this.productOverview || this;
      let properties = {};

      // Hidden input properties
      container
        .querySelectorAll('input[type="hidden"][name^="properties["]:not(:disabled)')
        .forEach((el) => {
          const propertyName = el.name.match(/\[([^)]+)\]/)?.[1];
          if (propertyName) properties[propertyName] = el.value;
        });

      // Promotion properties
      if (this.promotion && Object.keys(this.promotion).length > 0) {
        const promotionProperties = window.theme?.utilities?.getPromotionProperties(this.promotion);
        if (promotionProperties && Object.keys(promotionProperties).length > 0) {
          Object.entries(promotionProperties).forEach(([key, value]) => {
            properties[key] = value.value;
          });
        }
      }

      const variant = this.productOverview?.variant;
      if (variant?.color) {
        properties['_color_id'] = variant.color;
      }

      properties['_add_to_cart_component'] = 'product-form';

      if (this.allowDebug()) {
        console.log('ProductForm: collectLineItemProperties', properties);
      }

      return properties;
    }

    async submitToCart() {
      if (!this.atcButton) return;
      const variant = this.productOverview?.variant;
      if (!isVariantFullyAvailable(variant)) return;

      try {
        const monogramData = this.productOverview?.getMonogramAddToCartData?.() || null;
        const monogramProperties = monogramData?.properties && typeof monogramData.properties === 'object'
          ? monogramData.properties
          : {};
        const resolvedVariantId = Number(monogramData?.id);
        const currentVariantId = Number(variant.id);
        const lineItemVariantId =
          Number.isInteger(resolvedVariantId) && resolvedVariantId > 0
            ? resolvedVariantId
            : currentVariantId;

        if (!Number.isInteger(lineItemVariantId) || lineItemVariantId <= 0) {
          return;
        }

        const properties = { ...this.collectLineItemProperties(), ...monogramProperties };
        const item = {
          id: lineItemVariantId,
          quantity: 1,
          properties,
        };

        this.setAddingToBagState(true);

        if (this.allowDebug()) {
          console.log('ProductForm: submitToCart', item);
        }

        const { addToCart } = await import('@utilities/theme');
        await addToCart(item);

        this.handleErrorMessage();

        // Close quickview modal if present
        const quickview = this.closest('quickview-modal');
        if (quickview) quickview.close();

        this.setAddingToBagState(false);
      } catch (error) {
        console.error('ProductForm: Error adding to cart:', error);
        this.setAddingToBagState(false);
        this.handleErrorMessage(error.message || 'Unable to add this item to your bag.');
      }
    }

    handleErrorMessage(errorMessage = false) {
      if (!this.errorMessageWrapper) {
        this.errorMessageWrapper = this.querySelector('.product-form__error-message-wrapper');
      }
      if (!this.errorMessageWrapper) return;
      if (!this.errorMessage) {
        this.errorMessage = this.errorMessageWrapper.querySelector('.product-form__error-message');
      }

      this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

      if (errorMessage && this.errorMessage) {
        this.errorMessage.textContent = errorMessage;
      }
    }

    // ─── Sticky ATC ─────────────────────────────────────────
    toggleStickyATBFunctionality() {
      if (this.enableStickyATB) {
        this.enableStickyATBEvents();
        this.handleStickyATC();
      } else {
        this.disableStickyATBEvents();
      }
    }

    enableStickyATBEvents() {
      window.addEventListener('scroll', this.boundHandleStickyATC);
      window.addEventListener('resize', this.boundHandleResize);
      this.handleStickyATC();
    }

    disableStickyATBEvents() {
      window.removeEventListener('scroll', this.boundHandleStickyATC);
      window.removeEventListener('resize', this.boundHandleResize);
      this.resetStickyATBStyles();
    }

    resetStickyATBStyles() {
      if (this.productActions) {
        this.productActions.classList.remove(
          'product__form-actions--sticky',
          'product__form-actions--sticky-stop'
        );
      }
    }

    handleStickyATC() {
      if (!this.productActions || !this.enableStickyATB) return;

      const mainContent = document.getElementById('main-content');
      if (!mainContent) return;

      const mainContentRect = mainContent.getBoundingClientRect();
      const buttonWrapperRect = this.productActions.getBoundingClientRect();
      const scrollTop = window.scrollY;
      const totalHeaderHeight =
        parseInt(
          getComputedStyle(document.documentElement).getPropertyValue('--header-height'),
          10
        ) || 92;

      const shouldBeSticky =
        scrollTop > buttonWrapperRect.top + scrollTop - totalHeaderHeight;

      if (!shouldBeSticky) {
        this.productActions.classList.remove(
          'product__form-actions--sticky',
          'product__form-actions--sticky-stop'
        );
        return;
      }

      const mainContentBottom = mainContentRect.bottom;
      const viewportHeight = window.innerHeight;

      if (mainContentBottom <= viewportHeight) {
        this.productActions.classList.add('product__form-actions--sticky-stop');
        this.productActions.classList.remove('product__form-actions--sticky');
      } else {
        this.productActions.classList.add('product__form-actions--sticky');
        this.productActions.classList.remove('product__form-actions--sticky-stop');
      }
    }

    handleResize() {
      this.handleStickyATC();
    }
  });
}
